import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaArrowLeft,
    FaArrowRight,
    FaEdit,
    FaTrash,
    FaEye,
    FaUpload,
    FaRegFileImage,
    FaPlay,
    FaClock,
    FaCoins,
    FaSortAmountDown,
    FaSortAmountUp
} from 'react-icons/fa';
import { IoMdSearch, IoMdClose } from 'react-icons/io';
import { MdDeleteSweep } from 'react-icons/md';
import { toast } from 'react-toastify';

import FileUpload from '../../components/ul/FileUpload';
import NotFound from '../../components/ul/NotFound';
import Loader from '../../components/Loader';

// Import utilities
import { MAX_FILE_SIZE_IMAGE } from '../../components/imageType';
import { formatView } from '../../components/formatView';
import generateCaptcha from '../../components/captcha';
import Calling from '../../api/Calling';

const Game = () => {
    const [loader, setLoader] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const navigate = useNavigate();
    const [formInfo, setFormInfo] = useState({
        g_name: '',
        g_url: '',
        g_coin: 10,
        g_timing: 60,
        g_status: true,
        g_played: 0
    });
    const [data, setData] = useState([]);
    const [selectRecord, setSelectRecord] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [file, setFile] = useState(false);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [fileProcessing, setFileProcessing] = useState({
        thumbnail: false
    });
    const [fileStatus, setFileStatus] = useState({
        thumbnail: null
    });
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [previewGame, setPreviewGame] = useState(null);
    const [limit] = useState(50);
    const [sort, setSort] = useState('desc');
    const [sortField, setSortField] = useState('createdAt');
    const [currentGameThumbnail, setCurrentGameThumbnail] = useState('');

    const imageTypeMain = [
        'image/png',
        'image/jpg',
        'image/jpeg',
        'image/gif'
    ];

    const openPreviewModal = (item) => {
        setPreviewGame(item);
    };

    const closePreviewModal = () => {
        setPreviewGame(null);
    };

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getGame(page, limit, search, sort, sortField);
            if (resp?.status) {
                setData(resp?.data);
                setTotalPages(resp?.pagination?.totalPages || 1);

                // If in edit mode, update form with current data
                if (isEditMode && editId) {
                    const gameToEdit = resp?.data.find(game => game._id === editId);
                    if (gameToEdit) {
                        loadGameDataToForm(gameToEdit);
                    }
                }
            }
        } catch (error) {
            console.log(error);
            navigate('/Error');
        } finally {
            setLoader(false);
        }
    };

    const loadGameDataToForm = (gameData) => {
        if (!gameData) return;

        setFormInfo({
            g_name: gameData.g_name || '',
            g_url: gameData.g_url || '',
            g_coin: gameData.g_coin || 10,
            g_timing: gameData.g_timing || 60,
            g_status: gameData.g_status === false ? false : true,
            g_played: gameData.g_played || 0
        });
        setCurrentGameThumbnail(gameData.g_thumbnail || '');
    };

    // Fixed handleSubmit function with proper form validation
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Initialize validation errors object
        let validationErrors = {};
        let isValid = true;

        // Validate game name
        if (!formInfo.g_name.trim()) {
            validationErrors.g_name = 'Game name is required';
            isValid = false;
        }

        // Validate game URL
        if (!formInfo.g_url.trim()) {
            validationErrors.g_url = 'Game URL is required';
            isValid = false;
        } else if (!/^https?:\/\//.test(formInfo.g_url)) {
            validationErrors.g_url = 'URL must start with http:// or https://';
            isValid = false;
        }

        // Validate game timing
        if (!formInfo.g_timing) {
            validationErrors.g_timing = 'Game timing is required';
            isValid = false;
        } else if (formInfo.g_timing < 1) {
            validationErrors.g_timing = 'Game timing must be at least 1 second';
            isValid = false;
        }

        // Validate coin amount
        if (!formInfo.g_coin) {
            validationErrors.g_coin = 'Coin amount is required';
            isValid = false;
        } else if (formInfo.g_coin < 1) {
            validationErrors.g_coin = 'Game coins must be at least 1';
            isValid = false;
        }

        // Validate thumbnail for new games
        if (!isEditMode && !file) {
            validationErrors.thumbnail = 'Please select a valid thumbnail image';
            isValid = false;
        }

        // If there are validation errors, display them and stop form submission
        if (!isValid) {
            // Display validation errors
            Object.keys(validationErrors).forEach(field => {
                toast.error(validationErrors[field]);
            });
            return;
        }

        // If validation passes, proceed with form submission
        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('g_name', formInfo.g_name);
            formData.append('g_url', formInfo.g_url);
            formData.append('g_coin', formInfo.g_coin);
            formData.append('g_timing', formInfo.g_timing);
            if (isEditMode) {
                formData.append('g_status', formInfo.g_status);
                formData.append('g_played', formInfo.g_played);
            }
            if (thumbnailFile) {
                formData.append('image', thumbnailFile);
            }

            let resp;
            if (isEditMode) {
                resp = await Calling.updateGame(formData, editId);
            } else {
                resp = await Calling.addGame(formData);
            }

            if (resp?.status) {
                toast.success(resp?.message || (isEditMode ? 'Game updated successfully!' : 'Game added successfully!'));
                resetForm();
                setShowForm(false);
                fetchData();
            } else {
                toast.error(resp?.message || (isEditMode ? 'Failed to update game!' : 'Failed to add game!'));
            }
        } catch (error) {
            console.log(error);
            toast.error('An error occurred. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        // Handle different input types
        const processedValue = type === 'number' ? Number(value) :
            type === 'checkbox' ? checked : value;
        setFormInfo(prevInfo => ({ ...prevInfo, [name]: processedValue }));
    };

    const handleChangeFile = (e) => {
        const { name, files, id } = e.target;
        const file = files[0];

        if (file) {
            // Validate file type
            const isValidType = imageTypeMain.includes(file.type);
            if (!isValidType) {
                toast.error("Please select a valid image file. Allowed types: 'image/png', 'image/jpg', 'image/jpeg', 'image/gif'");
                setThumbnailFile(null);
                setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
                setFileStatus((prevState) => ({ ...prevState, [id]: 'error' }));
                setFile(false);
                e.target.value = '';
                return;
            }

            // Validate file size
            if (file.size > MAX_FILE_SIZE_IMAGE) {
                toast.error(`Image size should not exceed ${Math.round(MAX_FILE_SIZE_IMAGE / 1024 / 1024)}MB`);
                setThumbnailFile(null);
                setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
                setFileStatus((prevState) => ({ ...prevState, [id]: 'error' }));
                setFile(false);
                e.target.value = '';
                return;
            }

            // Start processing the file
            setFileProcessing((prevState) => ({ ...prevState, [id]: true }));
            setFileStatus((prevState) => ({ ...prevState, [id]: null }));

            // Simulate file processing
            setTimeout(() => {
                setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
                setFileStatus((prevState) => ({ ...prevState, [id]: 'success' }));
            }, 1000);

            setThumbnailFile(file);
            setFile(true);
        }
    };

    const handleSelect = (recordId) => {
        setSelectRecord((prev) =>
            selectRecord.includes(recordId)
                ? prev.filter(id => id !== recordId)
                : [...prev, recordId]
        );
    };

    const handleDeleteGame = async (id) => {
        const content = `You are about to delete the game(s). To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (id) {
                    body = { id: [id] };
                } else {
                    body = { id: selectRecord };
                }
                const response = await Calling.deleteGame(body);
                if (response?.status) {
                    toast.success(response?.message || "Game(s) deleted successfully.");
                    setSelectRecord([]);

                    // If the deleted game is currently being edited, reset the form
                    if (isEditMode && (id === editId || selectRecord.includes(editId))) {
                        resetForm();
                    }
                } else {
                    toast.error(response?.message || "Failed to delete game(s)");
                }
            } catch (error) {
                console.error("Error deleting games:", error);
                toast.error("An error occurred while deleting game(s)");
            } finally {
                setLoader(false);
                fetchData();
            }
        } else {
            toast.error("Incorrect answer to the captcha.", { autoClose: 2000 });
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleGameStatus = async (item) => {
        try {
            setLoader(true);
            const formData = new FormData();
            formData.append('g_status', !item?.g_status);
            formData.append('g_name', item.g_name);
            formData.append('g_url', item.g_url);
            formData.append('g_coin', item.g_coin);
            formData.append('g_timing', item.g_timing);
            formData.append('g_played', item.g_played);
            const resp = await Calling.updateGame(formData, item?._id);
            if (resp?.status) {
                toast.success(resp?.message || 'Status updated successfully!');

                // If this is the game currently being edited, update its status in the form
                if (isEditMode && editId === item._id) {
                    setFormInfo(prev => ({
                        ...prev,
                        g_status: !item.g_status
                    }));
                }
            } else {
                toast.error(resp?.message || 'Failed to update status!');
            }
        } catch (error) {
            console.log(error);
            toast.error('An error occurred while updating status');
        } finally {
            setLoader(false);
            fetchData();
        }
    };

    const handleEditGame = (id) => {
        // Find the game in the current data
        const gameToEdit = data.find(game => game._id === id);

        if (gameToEdit) {
            setShowForm(true);
            setIsEditMode(true);
            setEditId(id);
            loadGameDataToForm(gameToEdit);
        } else {
            toast.error("Game data not found");
        }
    };

    const resetForm = () => {
        setThumbnailFile(null);
        setFormInfo({
            g_name: '',
            g_url: '',
            g_coin: 10,
            g_timing: 60,
            g_status: true,
            g_played: 0
        });
        setFile(false);
        setFileProcessing({
            thumbnail: false
        });
        setFileStatus({
            thumbnail: null
        });
        setIsEditMode(false);
        setEditId(null);
        setCurrentGameThumbnail('');
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const handleSortChange = (field) => {
        if (sortField === field) {
            // If clicking the same field, toggle sort direction
            setSort(sort === 'asc' ? 'desc' : 'asc');
        } else {
            // If clicking a different field, set it as the sort field with default desc order
            setSortField(field);
            setSort('desc');
        }
    };

    useEffect(() => {
        fetchData();
    }, [search, page, sort, sortField]);

    return (
        <div className='md:ml-[4.4rem] w-full text-main-font mt-16 px-3'>
            <h1 className="text-2xl font-bold mt-4 text-main-font w-auto">Game Management</h1>

            <div className='bg-main-white rounded-xl shadow-md overflow-hidden'>

                {/* Header with toggle button */}
                <div className='p-6'>
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center items-start gap-4 mb-6">
                        {/* Title */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <h1 className="text-2xl font-bold text-main-font">Games</h1>
                            <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-600 flex items-center gap-1 font-medium">
                                <FaPlay size={16} className="text-blue-600" />
                                Manage Games
                            </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => {
                                    if (showForm && isEditMode) {
                                        resetForm();
                                        setShowForm(true);
                                    } else {
                                        setShowForm(!showForm);
                                        if (showForm) {
                                            resetForm();
                                        }
                                    }
                                }}
                                className="bg-main-blue hover:bg-main-blue/80 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
                            >
                                {showForm && isEditMode ? 'Add New Game' : (showForm ? 'Hide Form' : (
                                    <>
                                        <FaUpload className="text-sm" />
                                        Add New Game
                                    </>
                                ))}
                            </button>
                        </div>
                    </div>

                    <hr className='border-main-border mb-6' />

                    {/* Form with animation */}
                    <div className={`transition-all duration-500 ease-in-out overflow-hidden ${showForm ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="bg-gray-50 p-6 rounded-lg shadow-inner mb-8">
                            <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                                <FaPlay className="mr-2 text-blue-600" />
                                {isEditMode ? `Edit Game: ${formInfo.g_name}` : 'Add New Game'}
                            </h2>

                            <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-4">
                                {/* Form Fields */}
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                    <div className='flex flex-col'>
                                        <label className='text-gray-700 font-medium mb-2' htmlFor='g_name'>
                                            Game Name
                                        </label>
                                        <input
                                            type='text'
                                            id='g_name'
                                            name='g_name'
                                            value={formInfo.g_name}
                                            onChange={handleChange}
                                            className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                            placeholder='Enter Game Name'
                                            required
                                        />
                                    </div>
                                    <div className='flex flex-col'>
                                        <label className='text-gray-700 font-medium mb-2' htmlFor='g_url'>
                                            Game URL
                                        </label>
                                        <input
                                            type='url'
                                            id='g_url'
                                            name='g_url'
                                            value={formInfo.g_url}
                                            onChange={handleChange}
                                            className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                            placeholder='https://example.com/game'
                                            required
                                        />
                                    </div>
                                    <div className='flex flex-col'>
                                        <label className='text-gray-700 font-medium mb-2 flex items-center' htmlFor='g_coin'>
                                            <FaCoins className="mr-2 text-yellow-500" />
                                            Coins Reward
                                        </label>
                                        <input
                                            type='number'
                                            id='g_coin'
                                            name='g_coin'
                                            value={formInfo.g_coin}
                                            onChange={handleChange}
                                            min="1"
                                            step="1"
                                            className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                            placeholder='Coins rewarded for playing'
                                            required
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Coins rewarded to users for playing this game</p>
                                    </div>
                                    <div className='flex flex-col'>
                                        <label className='text-gray-700 font-medium mb-2 flex items-center' htmlFor='g_timing'>
                                            <FaClock className="mr-2 text-orange-500" />
                                            Gameplay Time (seconds)
                                        </label>
                                        <input
                                            type='number'
                                            id='g_timing'
                                            name='g_timing'
                                            value={formInfo.g_timing}
                                            onChange={handleChange}
                                            min="1"
                                            step="1"
                                            className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                            placeholder='Minimum gameplay time in seconds'
                                            required
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Minimum time (in seconds) users must play to earn coins</p>
                                    </div>

                                    {isEditMode && (
                                        <>
                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2 flex items-center' htmlFor='g_played'>
                                                    <FaPlay className="mr-2 text-purple-500" />
                                                    Play Count
                                                </label>
                                                <input
                                                    type='number'
                                                    id='g_played'
                                                    name='g_played'
                                                    value={formInfo.g_played}
                                                    onChange={handleChange}
                                                    min="0"
                                                    step="1"
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                    placeholder='Number of times played'
                                                />
                                            </div>
                                            <div className='flex flex-col justify-center'>
                                                <label className='text-gray-700 font-medium mb-2 flex items-center cursor-pointer'>
                                                    <input
                                                        type="checkbox"
                                                        id="g_status"
                                                        name="g_status"
                                                        checked={formInfo.g_status}
                                                        onChange={handleChange}
                                                        className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-2 focus:ring-blue-400 mr-2"
                                                    />
                                                    Active Status
                                                </label>
                                                <p className="text-xs text-gray-500 mt-1">Enable to make this game visible and playable</p>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* File Upload */}
                                <div className='flex flex-col'>
                                    <label className='text-gray-700 font-medium mb-2' htmlFor='thumbnail'>
                                        Game Thumbnail {isEditMode && '(Upload only to change)'}
                                    </label>
                                    {isEditMode && currentGameThumbnail && (
                                        <div className="mb-2">
                                            <img
                                                src={currentGameThumbnail}
                                                alt="Current thumbnail"
                                                className="h-20 w-32 object-cover rounded-md border border-gray-300"
                                            />
                                        </div>
                                    )}
                                    <div className='relative bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-main-blue transition-all duration-200'>
                                        <FileUpload
                                            id="thumbnail"
                                            name='thumbnail'
                                            accept='image/png,image/jpg,image/jpeg,image/gif'
                                            onChange={handleChangeFile}
                                            required={!isEditMode}
                                            className="relative z-10"
                                        />
                                        {fileProcessing['thumbnail'] ? (
                                            <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-lg'>
                                                <div className='w-8 h-8 border-4 border-t-main-blue border-blue-200 rounded-full animate-spin'></div>
                                            </div>
                                        ) : fileStatus['thumbnail'] === 'error' ? (
                                            <div className='absolute top-4 right-4 text-red-500'>✘</div>
                                        ) : fileStatus['thumbnail'] === 'success' ? (
                                            <div className='absolute top-4 right-4 text-green-500'>✓</div>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className='flex flex-col sm:flex-row gap-4 pt-4'>
                                    <button
                                        type="submit"
                                        className="bg-main-blue hover:bg-main-blue/80 cursor-pointer text-white px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                                        disabled={((!isEditMode && !file) || loader)}
                                    >
                                        {loader ? (
                                            <div className='w-5 h-5 border-2 border-t-white border-white/20 rounded-full animate-spin mr-2'></div>
                                        ) : (
                                            isEditMode ? <FaEdit className="mr-2" /> : <FaUpload className="mr-2" />
                                        )}
                                        {loader ? 'Processing...' : (isEditMode ? 'Update Game' : 'Add Game')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (isEditMode) {
                                                // Cancel edit mode
                                                resetForm();
                                                setShowForm(false);
                                            } else {
                                                // Just reset the form
                                                resetForm();
                                            }
                                        }}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                                    >
                                        {isEditMode ? 'Cancel Edit' : 'Reset Form'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <hr className='border-t-2 border-gray-100' />

                {/* Games Listing Section */}
                <div className='bg-main-white'>
                    <div className='p-6'>
                        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6'>
                            <h2 className='text-xl font-bold text-gray-800 mb-3 sm:mb-0'>
                                Your Games
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                    ({data.length} total)
                                </span>
                            </h2>
                            <div className="relative flex items-center gap-2 w-full sm:w-64">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <IoMdSearch className="text-gray-400" size={18} />
                                </div>
                                <input
                                    type="search"
                                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                    value={search}
                                    onChange={handleSearchChange}
                                    placeholder="Search games..."
                                />
                            </div>
                        </div>

                        {/* Table section with animation */}
                        <div className="bg-white rounded-lg shadow overflow-hidden transition-all duration-300">
                            <div className="overflow-x-auto">
                                <table className='w-full min-w-[1000px]'>
                                    <thead className='bg-gray-50 text-gray-700 uppercase'>
                                        <tr>
                                            <th className='px-6 py-4 text-left text-xs font-medium rounded-tl-lg'>
                                                <div className="flex items-center">
                                                    <input
                                                        type='checkbox'
                                                        className='w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-400'
                                                        onChange={(e) => setSelectRecord(e.target.checked ? data.map(v => v._id) : [])}
                                                        checked={data.length > 0 && selectRecord.length === data.length}
                                                    />
                                                    {selectRecord.length > 0 && (
                                                        <button
                                                            className="ml-3 bg-red-50 hover:bg-red-100 text-red-600 p-1 rounded transition-colors duration-200"
                                                            onClick={() => handleDeleteGame(null)}
                                                        >
                                                            <MdDeleteSweep size={20} />
                                                        </button>
                                                    )}
                                                </div>
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium">#</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium">Game</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium">URL</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium">Coins</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium">Timing</th>
                                            <th
                                                className="px-6 py-4 text-left text-xs font-medium cursor-pointer"
                                                onClick={() => handleSortChange('g_played')}
                                            >
                                                <div className="flex items-center">
                                                    <span>Plays</span>
                                                    {sortField === 'g_played' && (
                                                        <span className="ml-1">
                                                            {sort === 'desc' ? <FaSortAmountDown size={12} /> : <FaSortAmountUp size={12} />}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium">Status</th>
                                            <th
                                                className="px-6 py-4 text-left text-xs font-medium cursor-pointer"
                                                onClick={() => handleSortChange('createdAt')}
                                            >
                                                <div className="flex items-center">
                                                    <span>Date</span>
                                                    {sortField === 'createdAt' && (
                                                        <span className="ml-1">
                                                            {sort === 'desc' ? <FaSortAmountDown size={12} /> : <FaSortAmountUp size={12} />}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium">Actions</th>
                                        </tr>
                                    </thead>
                                    {loader ? (
                                        <tbody>
                                            <tr>
                                                <td colSpan={10} className="text-center py-8">
                                                    <Loader loader={loader} />
                                                </td>
                                            </tr>
                                        </tbody>
                                    ) : (
                                        <tbody>
                                            {data.length > 0 ? (
                                                data?.map((item, index) => (
                                                    <tr key={item._id} className={`hover:bg-blue-50 transition-colors duration-150 ${editId === item._id ? 'bg-blue-50' : ''}`}>
                                                        <td className="px-6 py-4 text-left">
                                                            <input
                                                                type='checkbox'
                                                                checked={selectRecord?.includes(item?._id)}
                                                                onChange={() => handleSelect(item?._id)}
                                                                className='w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-400'
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500">
                                                            {(page - 1) * limit + index + 1}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center">
                                                                <div className="h-12 w-16 flex-shrink-0">
                                                                    <img
                                                                        src={item?.g_thumbnail}
                                                                        alt="Thumbnail"
                                                                        className="h-12 w-16 rounded-md object-cover cursor-pointer"
                                                                        onClick={() => openPreviewModal(item)}
                                                                    />
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {item?.g_name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-blue-600">
                                                            <a
                                                                href={item?.g_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="hover:underline truncate inline-block max-w-[200px]"
                                                            >
                                                                {item?.g_url}
                                                            </a>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                <FaCoins className="mr-1" size={12} />
                                                                {item?.g_coin}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                                <FaClock className="mr-1" size={12} />
                                                                {item?.g_timing}s
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                <FaPlay className="mr-1" size={12} />
                                                                {formatView(item?.g_played)}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center">
                                                                <label className="relative inline-flex items-center cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={item?.g_status}
                                                                        onChange={() => handleGameStatus(item)}
                                                                        className="sr-only peer"
                                                                    />
                                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                                                </label>
                                                                <span className={`ml-2 text-xs ${item?.g_status ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {item?.g_status ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500">
                                                            {new Date(item.createdAt).toLocaleDateString('en-US', {
                                                                day: 'numeric',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            })}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm font-medium">
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() => handleEditGame(item?._id)}
                                                                    className={`text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 p-2 rounded-full transition-colors duration-200 ${editId === item._id ? 'ring-2 ring-blue-400' : ''}`}
                                                                >
                                                                    <FaEdit size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteGame(item?._id)}
                                                                    className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 p-2 rounded-full transition-colors duration-200"
                                                                >
                                                                    <FaTrash size={16} />
                                                                </button>
                                                                <a
                                                                    href={item.g_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 p-2 rounded-full transition-colors duration-200"
                                                                >
                                                                    <FaPlay size={16} />
                                                                </a>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={10}>
                                                        <NotFound />
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    )}
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {data.length > 0 && (
                                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200 rounded-b-lg">
                                    <div className="flex gap-2">
                                        <button
                                            className={`px-4 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 ${page === 1
                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                : "bg-main-blue text-white hover:bg-main-blue/80"
                                                }`}
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={page === 1}
                                        >
                                            <FaArrowLeft size={14} /> Previous
                                        </button>
                                        <button
                                            className={`px-4 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 ${page === totalPages
                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                : "bg-main-blue text-white hover:bg-main-blue/80"
                                                }`}
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                        >
                                            Next <FaArrowRight size={14} />
                                        </button>
                                    </div>

                                    <div className="flex items-center text-sm text-gray-700">
                                        <span>Page</span>
                                        <span className="font-medium mx-2 bg-white border border-gray-300 rounded-md px-3 py-1">
                                            {page < 10 ? `0${page}` : page}
                                        </span>
                                        <span>of {totalPages < 10 ? `0${totalPages}` : totalPages}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Game Preview Modal */}
            {previewGame && (
                <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg overflow-hidden max-w-3xl w-full mx-auto max-h-[90vh]">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between bg-gray-100 px-6 py-3">
                            <h3 className="font-medium text-gray-800">{previewGame?.g_name}</h3>
                            <button
                                onClick={closePreviewModal}
                                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-200 transition-colors duration-200"
                            >
                                <IoMdClose size={24} />
                            </button>
                        </div>

                        {/* Game Content */}
                        <div className="p-6">
                            <div className="mb-6 bg-gray-100 rounded-lg overflow-hidden">
                                <img
                                    src={previewGame?.g_thumbnail}
                                    alt={previewGame?.g_name}
                                    className="w-full h-auto object-cover"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Game URL</h4>
                                    <a
                                        href={previewGame?.g_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline break-all"
                                    >
                                        {previewGame?.g_url}
                                    </a>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Status</h4>
                                    <span className={`${previewGame?.g_status ? 'text-green-600' : 'text-red-600'} font-medium`}>
                                        {previewGame?.g_status ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Coin Reward</h4>
                                    <div className="flex items-center">
                                        <FaCoins className="text-yellow-500 mr-2" />
                                        <span className="font-medium">{previewGame?.g_coin} coins</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Required Time</h4>
                                    <div className="flex items-center">
                                        <FaClock className="text-orange-500 mr-2" />
                                        <span className="font-medium">{previewGame?.g_timing} seconds</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Total Plays</h4>
                                    <div className="flex items-center">
                                        <FaPlay className="text-purple-500 mr-2" />
                                        <span className="font-medium">{formatView(previewGame?.g_played)}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Added On</h4>
                                    <span className="font-medium">
                                        {new Date(previewGame?.createdAt).toLocaleDateString('en-US', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric'
                                        })}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6 flex gap-3">
                                <a
                                    href={previewGame?.g_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-main-blue hover:bg-main-blue/80 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
                                >
                                    <FaPlay className="mr-2" size={16} />
                                    Play Game
                                </a>
                                <button
                                    onClick={() => {
                                        closePreviewModal();
                                        handleEditGame(previewGame?._id);
                                    }}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
                                >
                                    <FaEdit className="mr-2" size={16} />
                                    Edit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Game;